import { Button } from '@/components/ui/button'
import { FileText, Phone, PhoneOff, ShoppingBag as ShoppingBagIcon } from 'lucide-react'
import { Message } from '@/hooks/useConversationMessages'
import { cn } from '@/lib/utils'
import { parseWhatsAppFormatting } from './messageUtils'
import { OrderPlaced } from './OrderPlaced'
import { ListMessage } from './ListMessage'
import { ProductList } from './ProductList'
import { OrderDetailsRequest } from './OrderDetailsRequest'

interface InteractiveMessageProps {
  message: Message
}

export function InteractiveMessage({ message }: InteractiveMessageProps) {
  let interactiveData = null
  let displayText = message.content_text || ''
  let buttons: any[] = []
  
  const mediaUrl = (message as any).mediaUrl || (message as any).media_url
  const mediaType = message.media_type || (message as any).mediaType

  if (mediaType === 'list') {
    return <ListMessage message={message} />
  }

  try {
    const contentData = JSON.parse(message.content_text || '{}')

    const sections = contentData.sections || contentData.action?.sections
    if ((sections && Array.isArray(sections) && sections.length > 0) || contentData.type === 'list') {
      return <ListMessage message={message} />
    }

    if (contentData.interactiveData && typeof contentData.interactiveData === 'object') {
      interactiveData = contentData.interactiveData
      displayText = contentData.text || displayText

      if (interactiveData.type === 'order_placed') {
        return <OrderPlaced interactionData={interactiveData} />
      }

      if (interactiveData.type === 'call_permission_reply') {
        const accepted = interactiveData.response === 'accept'
        const expiry = interactiveData.expiration_timestamp
          ? new Date(interactiveData.expiration_timestamp * 1000).toLocaleDateString()
          : null
        return (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-xl border text-sm ${accepted ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            {accepted
              ? <Phone className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
              : <PhoneOff className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />}
            <div>
              <p className="font-medium">{accepted ? 'Call permission granted' : 'Call permission declined'}</p>
              {accepted && expiry && <p className="text-xs opacity-70 mt-0.5">Valid until {expiry}{interactiveData.is_permanent ? ' (permanent)' : ''}</p>}
            </div>
          </div>
        )
      }
    } else if (contentData.type === 'button') {
      interactiveData = {
        type: 'button',
        header: contentData.header?.text || null,
        footer: contentData.footer?.text || null,
      }
      displayText = contentData.body?.text || ''
      buttons = (contentData.action?.buttons || []).map((btn: any) => ({
        id: btn.reply?.id || btn.id || '',
        text: btn.reply?.title || btn.title || '',
      }))
    } else if (contentData.type === 'order_details') {
      return <OrderDetailsRequest interactiveData={contentData} />
    } else if (contentData.type === 'catalogmessage' || contentData.type === 'catalog_message') {
      return (
        <div
          className="overflow-hidden max-w-xs"
          style={{ fontFamily: 'var(--wn-font)' }}
        >
          <div className="flex items-center gap-2.5 px-3 pt-3 pb-2.5">
            <div
              className="h-9 w-9 rounded-[10px] flex items-center justify-center shrink-0"
              style={{ background: '#E8F8F0' }}
            >
              <ShoppingBagIcon className="h-4 w-4" style={{ color: '#1EB677' }} />
            </div>
            <div style={{ fontFamily: 'var(--wn-font)', fontSize: 13, fontWeight: 700, color: '#101828' }}>
              Catalog
            </div>
          </div>
          {contentData.body?.text && (
            <div
              className="px-3 pb-3 whitespace-pre-wrap"
              style={{ fontFamily: 'var(--wn-font)', fontSize: 13, fontWeight: 500, color: '#101828', lineHeight: 1.5 }}
            >
              {contentData.body.text}
            </div>
          )}
          <div
            className="border-t border-[#edf1f7] px-3 py-2.5 text-center"
            style={{ fontFamily: 'var(--wn-font)', fontSize: 13, fontWeight: 600, color: '#1EB677' }}
          >
            View Catalog
          </div>
        </div>
      )
    } else if (contentData.type === 'productlist' || contentData.type === 'product_list') {
      const nativeSections = contentData.action?.sections || []
      const totalProducts = nativeSections.reduce(
        (sum: number, s: any) => sum + (s.product_items?.length || s.productitems?.length || 0), 0
      )
      return (
        <ProductList
          interactiveData={{
            header_text: contentData.header?.text || '',
            body_text: contentData.body?.text || '',
            footer_text: contentData.footer?.text || '',
            total_items: totalProducts,
            sections: nativeSections.map((s: any) => ({
              title: s.title,
              product_items: s.product_items || s.productitems || [],
            })),
          }}
        />
      )
    } else if (contentData.quickReplies && Array.isArray(contentData.quickReplies)) {
      displayText = contentData.text || ''
      buttons = contentData.buttons || contentData.quickReplies.map((reply: string, index: number) => ({
        id: `quick_reply_${index}`,
        text: reply,
        payload: reply,
        type: "QUICK_REPLY",
        position: index,
      }))
      interactiveData = {
        type: 'quick_reply',
        quickReplies: contentData.quickReplies,
        header: contentData.headerText || null,
      }
    } else if (contentData.type === 'whatsapp_flow') {
      interactiveData = {
        type: 'whatsapp_flow',
        flowName: contentData.flowName,
        header: contentData.headerText || null,
        footer: contentData.footerText || null,
      }
      displayText = contentData.bodyText || contentData.ctaLabel || 'Tap below to continue.'
    } else if (contentData.type === 'interactive') {
      interactiveData = contentData
      displayText = contentData.text || contentData.display_text || ''
      buttons = contentData.buttons || []
    } else if (contentData.type === 'cta_url' || contentData.type === 'ctaurl') {
      const params = contentData.action?.parameters || {}
      interactiveData = {
        type: 'cta_url',
        header: contentData.header?.text || null,
        footer: contentData.footer?.text || null,
        ctaUrl: params.url || null,
        ctaLabel: params.display_text || params.displaytext || 'Open link',
      }
      displayText = contentData.body?.text || ''
    } else {
      displayText = message.content_text || ''
    }
  } catch (e) {
    displayText = message.content_text || ''
  }

  const isOutbound = !message.is_inbound

  return (
    <div className="space-y-3 min-w-0 max-w-full" data-testid="inbox-interactive-message">
      {mediaUrl && mediaType && (
        <div className="mb-2 min-w-0">
          {mediaType === 'image' && (
            <img
              src={mediaUrl}
              alt={displayText || 'Image'}
              loading="lazy"
              decoding="async"
              className="max-w-full rounded-lg"
              style={{ maxHeight: '300px', objectFit: 'contain' }}
            />
          )}
          {mediaType === 'video' && (
            <video
              src={mediaUrl}
              controls
              className="max-w-full rounded-lg"
              style={{ maxHeight: '300px' }}
            >
              Your browser does not support the video tag.
            </video>
          )}
          {mediaType === 'audio' && (
            <audio src={mediaUrl} controls className="w-full">
              Your browser does not support the audio tag.
            </audio>
          )}
          {mediaType === 'document' && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <FileText className="h-5 w-5 text-gray-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {(message as any).mediaFilename || 'Document'}
                </p>
                <a
                  href={mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline"
                >
                  Download
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {interactiveData?.header && (
        <div className="text-sm font-semibold mb-2 text-gray-900 break-words">
          {interactiveData.header}
        </div>
      )}

      {displayText && displayText.trim() && (
        <div className="text-sm whitespace-pre-wrap break-words leading-relaxed text-gray-900">
          {displayText.split('\n').map((line, index) => (
            <div key={index} className="mb-1">
              {parseWhatsAppFormatting(line)}
            </div>
          ))}
        </div>
      )}

      {interactiveData?.type === 'whatsapp_flow' && (
        <div className="mt-3 min-w-0">
          <Button
            variant="outline"
            className={cn(
              "w-full max-w-full !h-auto min-h-[36px] whitespace-normal break-words justify-center text-xs font-medium py-2 px-3 rounded-md border transition-all",
              "bg-green-50 hover:bg-green-100 text-green-700 border-green-200 hover:border-green-300 shadow-sm"
            )}
            size="sm"
            disabled={true}
          >
            <span className="text-xs break-words whitespace-normal leading-snug">
              {(() => {
                try {
                  const contentData = JSON.parse(message.content_text || '{}')
                  return contentData.ctaLabel || 'Open form'
                } catch {
                  return 'Open form'
                }
              })()}
            </span>
          </Button>
        </div>
      )}

      {interactiveData?.type === 'cta_url' && interactiveData.ctaUrl && (
        <div className="mt-3 min-w-0">
          <a
            href={interactiveData.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex w-full max-w-full min-w-0 items-center justify-center gap-1 text-xs font-medium py-1.5 px-3 rounded-md border transition-all",
              "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200 hover:border-blue-300 shadow-sm"
            )}
          >
            <span className="text-xs truncate min-w-0">{interactiveData.ctaLabel}</span>
          </a>
        </div>
      )}

      {interactiveData?.footer && (
        <div className="text-xs mt-2 text-gray-600 break-words">
          {interactiveData.footer}
        </div>
      )}

      {interactiveData && buttons && buttons.length > 0 && (
        <div className="flex flex-col gap-1.5 mt-3 min-w-0 w-full" data-testid="inbox-interactive-options">
          {buttons.map((btn: any, idx: number) => {
            const buttonText = btn.text || btn.reply?.title || btn.title || `Option ${idx + 1}`
            const buttonId = btn.id || btn.reply?.id || `btn-${idx}`

            return (
              <button
                key={buttonId}
                type="button"
                disabled
                data-testid="inbox-interactive-option"
                className={cn(
                  "w-full min-w-0 rounded-md border px-3 py-2 text-center text-xs font-medium shadow-sm cursor-default opacity-100",
                  isOutbound
                    ? "bg-white text-gray-900 border-gray-200"
                    : "bg-gray-50 text-gray-800 border-gray-200"
                )}
              >
                <span className="block break-words whitespace-normal leading-snug">
                  {buttonText}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {!mediaUrl && !displayText && !buttons.length &&
        !(interactiveData?.type === 'cta_url' && interactiveData.ctaUrl) && (
        <p className="text-sm text-muted-foreground italic">Interactive message</p>
      )}
    </div>
  )
}

